// assets/stamp-customizer.js
import { Component } from '@theme/component';
import { getTemplate, STAMP_FONTS, STAMP_INKS } from '@theme/stamp-templates';

/**
 * @typedef {object} StampCustomizerRefs
 * @property {HTMLInputElement[]} presetRadios - The preset selector radio inputs.
 * @property {SVGGElement} rectGroup - Rectangle-shape preview elements.
 * @property {SVGGElement} roundGroup - Round-shape preview elements.
 * @property {SVGRectElement} previewRect - The rectangle preview border.
 * @property {SVGTextElement} previewLine1 - Rectangle preview text, line 1.
 * @property {SVGTextElement} previewLine2 - Rectangle preview text, line 2.
 * @property {SVGTextElement} previewLine3 - Rectangle preview text, line 3.
 * @property {SVGTextElement} ringMeasure - Off-canvas text node used only to measure ring text width.
 * @property {SVGTextElement} centerMeasure - Off-canvas text node used only to measure center text width.
 * @property {SVGGElement} ringTextGroup - Container the ring's per-character glyphs are rendered into (single-star layout's only arc; the top arc in the two-star layout).
 * @property {SVGGElement} ringTextGroup2 - Container for the bottom arc's per-character glyphs, used only by the two-star layout.
 * @property {SVGGElement} centerTextGroup - Container the center text's wrapped lines are rendered into.
 * @property {SVGGElement} starsGroup - Container the star(s) are rebuilt into on every preset change.
 * @property {SVGGElement} forGroup - "For" signature stamp preview elements.
 * @property {SVGTextElement} forText - "For" signature stamp's fixed "For" + typed company/name line.
 * @property {SVGTextElement} forDesignationText - "For" signature stamp's fixed designation line (Partner/Proprietor/etc), bottom right.
 * @property {HTMLInputElement} lineInput1 - Text input, line 1 (ring text / top arc text for round; line 1 for rectangle; company/name for the "for" shape).
 * @property {HTMLInputElement} lineInput2 - Text input, line 2 (bottom arc text for the two-star round layout; line 2 for rectangle).
 * @property {HTMLTextAreaElement} centerInput - Textarea for the round layouts' center text; supports Enter for an explicit line break.
 * @property {HTMLInputElement} lineInput3 - Text input, line 3 (rectangle only).
 * @property {HTMLElement} fieldWrapper1 - Wrapper for input 1's field.
 * @property {HTMLElement} fieldWrapper2 - Wrapper for input 2's field.
 * @property {HTMLElement} fieldWrapperCenter - Wrapper for the center text field.
 * @property {HTMLElement} fieldWrapper3 - Wrapper for input 3's field.
 * @property {HTMLElement} fieldLabel1 - Label for input 1.
 * @property {HTMLElement} fieldLabel2 - Label for input 2.
 * @property {HTMLElement} fieldLabelCenter - Label for the center text field.
 * @property {HTMLElement} fieldLabel3 - Label for input 3.
 * @property {HTMLElement} sizeControls - Wrapper for the round-only font-size controls.
 * @property {HTMLSelectElement} ringSizeInput - Ring text point-size dropdown.
 * @property {HTMLSelectElement} centerSizeInput - Center text point-size dropdown.
 * @property {HTMLInputElement} linkSizes - "Keep both sizes the same" checkbox.
 * @property {HTMLElement} fitNote - Status note wrapper.
 * @property {HTMLElement} fitNoteText - Status note text.
 * @property {HTMLInputElement} designInput - Hidden input carrying the selected preset name.
 * @property {HTMLInputElement} layoutNoteInput - Hidden input describing the layout in plain language.
 * @property {HTMLInputElement} ringSizePropInput - Hidden input carrying the ring text size.
 * @property {HTMLInputElement} centerSizePropInput - Hidden input carrying the center text size.
 * @property {HTMLInputElement} line1TextProp - Hidden input carrying line 1's final (cased) text.
 * @property {HTMLInputElement} line2TextProp - Hidden input carrying line 2's final (cased) text.
 * @property {HTMLInputElement} centerTextProp - Hidden input carrying the center text's final (cased) text.
 * @property {HTMLInputElement} line3TextProp - Hidden input carrying line 3's final (cased) text.
 * @property {HTMLInputElement} productionSvgInput - Hidden input carrying a standalone production-ready SVG of the design, merchant-only.
 */

// Same numbers shoppers already know from the font-size box in Word or Google
// Docs, not raw CSS pixels — there's no literal "point" on a stamp, but this is
// a far more familiar unit than an arbitrary pixel slider.
const POINT_SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20];
const PT_TO_PX = 4 / 3; // standard 96dpi point-to-pixel ratio

// Ring geometry: the circle the per-character glyphs sit on, centered on the
// 260x260 viewBox. 93 is the midpoint between the second outer circle (r=112)
// and the inner circle (r=74) — i.e. dead center of the band the ring/arc text
// actually writes into, not an arbitrary radius.
const RING_CX = 130;
const RING_CY = 130;
const RING_RADIUS = 93;
const RING_EDGE_MARGIN = 18; // ~ one space-width gap on each side of a star, total

// Single-star layout: one arc sweeping 320deg, leaving a 40deg gap at the bottom for the star.
const SINGLE_STAR_SWEEP_DEGREES = 320;
const SINGLE_STAR_ARC_LENGTH = RING_RADIUS * ((SINGLE_STAR_SWEEP_DEGREES * Math.PI) / 180);
const SINGLE_STAR_MAX_LENGTH = SINGLE_STAR_ARC_LENGTH - RING_EDGE_MARGIN;

// Two-star layout: top and bottom arcs each sweep 150deg, leaving gaps at both the left
// and right ends of the horizontal diameter for the two fixed stars.
const DOUBLE_STAR_SWEEP_DEGREES = 150;
const DOUBLE_STAR_ARC_LENGTH = RING_RADIUS * ((DOUBLE_STAR_SWEEP_DEGREES * Math.PI) / 180);
const DOUBLE_STAR_MAX_LENGTH = DOUBLE_STAR_ARC_LENGTH - RING_EDGE_MARGIN;

// Center text geometry: lines are centered as a block on the inner circle's center.
const CENTER_CX = 130;
const CENTER_CY = 130;
const CENTER_MAX_LENGTH = 132; // per-line usable width inside the inner circle
const MAX_CENTER_LINES = 3;
const CENTER_LINE_HEIGHT_EM = 1.15; // line-to-line spacing, relative to font-size px

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Shows/hides an SVG element via the `hidden` attribute, which is what the
 * block's stylesheet keys off. Assigning to `.hidden` would not work here:
 * that property is defined on HTMLElement, not SVGElement, so on a <g> it just
 * creates an unused JS property and leaves the attribute untouched.
 * @param {SVGElement | undefined} element
 * @param {boolean} hidden
 */
function setSvgHidden(element, hidden) {
  element?.toggleAttribute('hidden', hidden);
}

// Matches the star polygon baked into the block's SVG markup.
const STAR_POINTS =
  '0,-6 1.411,-1.942 5.706,-1.854 2.283,0.7416 3.527,4.854 0,2.4 -3.527,4.854 -2.283,0.7416 -5.706,-1.854 -1.411,-1.942';
// Fixed star position(s) for each layout: bottom-center for single, or the two
// ends of the horizontal diameter for double.
const STAR_SINGLE_POSITIONS = [[130, 225]];
const STAR_DOUBLE_POSITIONS = [
  [225, 130],
  [35, 130],
];

const RECT_LINE_Y = [115, 140, 165];

// "For" shape: the name line starts at x=20 and must not run past the right
// edge of the die. Like the ring arcs, the line stops accepting characters once
// it is physically full rather than letting the text overrun the stamp.
const FOR_TEXT_MAX_WIDTH = 215;

/**
 * Lets a shopper pick a preset stamp layout and live-preview their own text in it.
 * @extends Component<StampCustomizerRefs>
 */
class StampCustomizer extends Component {
  /** @type {'round' | 'rectangle' | 'for'} */
  #shape = 'round';
  /** @type {'single' | 'double'} */
  #starLayout = 'single';
  /** @type {string[]} */
  #placeholders = ['', '', ''];
  /** @type {boolean} */
  #uppercase = false;
  /** @type {string} - fixed designation text (e.g. "Partner"), "for" shape only */
  #designationText = '';
  /** @type {{name: string, hex: string}} - the ink the stamp prints in */
  #ink = { name: 'Black', hex: '#1a1a1a' };
  /** @type {{name: string, css: string, family: string}} - the typeface */
  #font = { name: 'Clean Sans', css: "'Inter', Arial, sans-serif", family: 'Inter' };
  /** @type {string} - data URL of an uploaded logo/image, or '' when none */
  #logoHref = '';
  /** @type {number} - logo width/height in viewBox units */
  #logoScale = 64;
  /** @type {'top' | 'center' | 'bottom'} - logo vertical placement */
  #logoPos = 'center';

  connectedCallback() {
    super.connectedCallback();

    // Adopt whichever ink swatch is pre-selected in the markup, if any.
    const initialInk = (this.refs.inkSwatches ?? []).find((s) => s.classList.contains('is-selected'));
    if (initialInk?.dataset.hex) {
      this.#ink = { name: initialInk.dataset.ink ?? 'Black', hex: initialInk.dataset.hex };
    }

    // Adopt whichever font swatch is pre-selected in the markup, if any.
    const initialFont = (this.refs.fontSwatches ?? []).find((s) => s.classList.contains('is-selected'));
    if (initialFont?.dataset.css) {
      this.#font = {
        name: initialFont.dataset.font ?? 'Clean Sans',
        css: initialFont.dataset.css,
        family: initialFont.dataset.family ?? 'Arial',
      };
    }

    const radios = this.refs.presetRadios ?? [];
    const initial = radios.find((radio) => radio.checked) ?? radios[0];

    if (initial) this.#applyPreset(initial);

    this.#syncInk();
    this.#applyFont();

    // Preload a design chosen in the "Browse designs" gallery (?template=<id>),
    // or switch to upload mode when arriving from the "Upload your own" tile.
    try {
      const params = new URLSearchParams(window.location.search);
      const tid = params.get('template');
      if (tid) {
        const tpl = getTemplate(tid);
        if (tpl) this.#applyTemplate(tpl);
      }
      if (params.get('mode') === 'upload') this.#setMode('upload');
    } catch (e) {
      /* no-op */
    }

    // Web fonts load asynchronously; the arc/wrap fitting measures glyph widths,
    // so re-render once fonts are ready so measurements use the real typeface.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => this.#rerender()).catch(() => {});
    }
  }

  /**
   * Preloads a full design from the gallery catalogue: layout + font + ink +
   * the sample text (which the shopper then edits). Mirrors #applyPreset's
   * group/field toggling but sets field VALUES (not placeholders).
   * @param {import('@theme/stamp-templates').StampTemplate} tpl
   */
  #applyTemplate(tpl) {
    const shape = tpl.shape === 'rectangle' ? 'rectangle' : tpl.shape === 'for' ? 'for' : 'round';
    const starLayout = tpl.starLayout === 'double' ? 'double' : 'single';
    const isRound = shape === 'round';
    const isRect = shape === 'rectangle';
    const isFor = shape === 'for';
    const isDouble = isRound && starLayout === 'double';

    this.#shape = shape;
    this.#starLayout = starLayout;
    this.#uppercase = tpl.uppercase !== false;
    this.#designationText = tpl.designation ?? '';
    this.#placeholders = ['', '', ''];

    // Font + ink from the template.
    const f = STAMP_FONTS[tpl.font];
    if (f) {
      this.#font = { name: tpl.font, css: f.css, family: f.family };
      (this.refs.fontSwatches ?? []).forEach((s) => s.classList.toggle('is-selected', s.dataset.font === tpl.font));
      this.#applyFont();
    }
    const inkHex = STAMP_INKS[tpl.ink];
    if (inkHex) {
      this.#ink = { name: tpl.ink, hex: inkHex };
      (this.refs.inkSwatches ?? []).forEach((s) => s.classList.toggle('is-selected', s.dataset.ink === tpl.ink));
      this.#syncInk();
    }

    const {
      rectGroup, roundGroup, forGroup, sizeControls,
      lineInput1, lineInput2, lineInput3, centerInput,
      fieldWrapper1, fieldWrapper2, fieldWrapper3, fieldWrapperCenter,
      fieldLabel1, fieldLabel2, fieldLabelCenter,
    } = this.refs;

    setSvgHidden(rectGroup, !isRect);
    setSvgHidden(roundGroup, !isRound);
    setSvgHidden(forGroup, !isFor);
    if (sizeControls) sizeControls.hidden = !isRound;

    if (isRound) {
      this.#buildStars(isDouble ? 'double' : 'single');
      if (lineInput1) lineInput1.value = tpl.ring ?? '';
      if (fieldWrapper1) fieldWrapper1.hidden = false;
      if (fieldLabel1) fieldLabel1.textContent = isDouble ? 'Top text' : 'Outer ring text';
      if (lineInput2) lineInput2.value = isDouble ? tpl.bottom ?? '' : '';
      if (fieldWrapper2) fieldWrapper2.hidden = !isDouble;
      if (fieldLabel2) fieldLabel2.textContent = 'Bottom text';
      if (fieldWrapper3) fieldWrapper3.hidden = true;
      if (centerInput) centerInput.value = tpl.center ?? '';
      if (fieldWrapperCenter) fieldWrapperCenter.hidden = false;
      if (fieldLabelCenter) fieldLabelCenter.textContent = 'Circle text';
      if (this.refs.ringSizeInput) this.refs.ringSizeInput.value = '11';
      if (this.refs.centerSizeInput) this.refs.centerSizeInput.value = '11';
      if (this.refs.linkSizes) this.refs.linkSizes.checked = true;
      this.#renderRound();
    } else if (isFor) {
      if (lineInput1) lineInput1.value = tpl.company ?? '';
      if (fieldWrapper1) fieldWrapper1.hidden = false;
      if (fieldLabel1) fieldLabel1.textContent = 'Company / name';
      if (fieldWrapper2) fieldWrapper2.hidden = true;
      if (fieldWrapperCenter) fieldWrapperCenter.hidden = true;
      if (fieldWrapper3) fieldWrapper3.hidden = true;
      this.#renderFor();
    } else {
      if (fieldWrapperCenter) fieldWrapperCenter.hidden = true;
      const lines = tpl.lines ?? [];
      for (let i = 1; i <= 3; i += 1) {
        const input = this.refs[`lineInput${i}`];
        const wrapper = this.refs[`fieldWrapper${i}`];
        const label = this.refs[`fieldLabel${i}`];
        if (!input) continue;
        input.value = lines[i - 1] ?? '';
        if (wrapper) wrapper.hidden = i > lines.length;
        if (label) label.textContent = `Line ${i}`;
      }
      this.#renderRectangle();
    }
  }

  /**
   * Applies the current font to the live preview (a CSS var every text element
   * inherits) and writes the Font line-item property. The production SVG picks
   * up the family on the next render.
   */
  #applyFont() {
    this.style.setProperty('--stamp-font', this.#font.css);
    this.#setProp(this.refs.fontProp, 'properties[Font]', this.#font.name);
  }

  /** @param {Event} event */
  handleFontChange(event) {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-font]');
    if (!btn || !btn.dataset.css) return;
    this.#font = {
      name: btn.dataset.font ?? 'Clean Sans',
      css: btn.dataset.css,
      family: btn.dataset.family ?? 'Arial',
    };
    (this.refs.fontSwatches ?? []).forEach((s) => s.classList.toggle('is-selected', s === btn));
    this.#applyFont();
    this.#rerender();
    // Re-measure once this face has finished loading, in case it wasn't cached.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => this.#rerender()).catch(() => {});
    }
  }

  /**
   * Recolors the live preview to the current ink and writes the Ink Colour
   * line-item property. The whole SVG is drawn in currentColor, so setting the
   * root color repaints borders, text and stars in one shot.
   */
  #syncInk() {
    const { previewSvg, inkProp } = this.refs;
    if (previewSvg) previewSvg.style.color = this.#ink.hex;
    this.#setProp(inkProp, 'properties[Ink Colour]', this.#ink.name);
  }

  /** Re-renders whichever shape is active (e.g. after an ink change). */
  #rerender() {
    if (this.#shape === 'rectangle') this.#renderRectangle();
    else if (this.#shape === 'for') this.#renderFor();
    else this.#renderRound();
  }

  /** @param {Event} event */
  handleInkChange(event) {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-ink]');
    if (!btn || !btn.dataset.hex) return;
    this.#ink = { name: btn.dataset.ink ?? 'Black', hex: btn.dataset.hex };
    (this.refs.inkSwatches ?? []).forEach((s) => s.classList.toggle('is-selected', s === btn));
    this.#syncInk();
    // Rebuild so the production SVG picks up the new ink hex too.
    this.#rerender();
  }

  handlePresetChange(event) {
    const radio = /** @type {HTMLInputElement} */ (event.target);
    this.#applyPreset(radio);
  }

  handleLineInput(event) {
    if (this.#shape === 'rectangle') {
      this.#renderRectangle();
      return;
    }

    if (this.#shape === 'for') {
      this.#renderFor();
      return;
    }

    if (event.target === this.refs.centerInput) {
      this.#handleCenterTyping(this.refs.centerInput);
      return;
    }

    const maxLength = this.#starLayout === 'double' ? DOUBLE_STAR_MAX_LENGTH : SINGLE_STAR_MAX_LENGTH;

    if (event.target === this.refs.lineInput1) {
      this.#handleArcTyping(this.refs.lineInput1, maxLength);
    } else if (this.#starLayout === 'double' && event.target === this.refs.lineInput2) {
      this.#handleArcTyping(this.refs.lineInput2, maxLength);
    }
  }

  handleRingSizeInput() {
    this.#applySize('ring', Number(this.refs.ringSizeInput.value), {});
  }

  handleCenterSizeInput() {
    this.#applySize('center', Number(this.refs.centerSizeInput.value), {});
  }

  handleLinkToggle() {
    if (this.refs.linkSizes?.checked) {
      // Ring is the reference size when the two get re-linked.
      this.#applySize('ring', Number(this.refs.ringSizeInput.value), {});
    }
  }

  #shapeLabel() {
    return this.#shape === 'rectangle' ? 'Rectangle' : this.#shape === 'for' ? '"For" signature stamp' : 'Round';
  }

  /** Opens the proof overlay with an enlarged copy of the current preview. */
  handleProofOpen() {
    const { previewSvg, proofStage, proofSummary, proofOverlay } = this.refs;
    if (!proofStage || !proofOverlay) return;

    if (previewSvg) {
      const clone = /** @type {SVGSVGElement} */ (previewSvg.cloneNode(true));
      clone.removeAttribute('ref');
      // Keep the base class too: the `[hidden]` groups (e.g. the rectangle
      // layout when a round design is active) only collapse via the
      // `.stamp-customizer__svg [hidden] { display:none }` rule — SVG doesn't
      // honour the bare `hidden` attribute — so a clone without it would leak
      // the inactive layout's shapes into the proof.
      clone.setAttribute('class', 'stamp-customizer__svg stamp-customizer__proof-svg');
      proofStage.replaceChildren(clone);
    }
    if (proofSummary) {
      proofSummary.textContent = `${this.#shapeLabel()} · ${this.#ink.name} ink · ${this.#font.name}`;
    }
    proofOverlay.hidden = false;
  }

  handleProofClose() {
    if (this.refs.proofOverlay) this.refs.proofOverlay.hidden = true;
  }

  /** Confirms the proof: records it as a line-item property and marks the button. */
  handleProofConfirm() {
    this.#setProp(this.refs.proofProp, 'properties[Proof reviewed]', 'Yes');
    this.#generateProofImage();
    const { proofButton } = this.refs;
    if (proofButton) {
      proofButton.textContent = 'Proof reviewed ✓';
      proofButton.classList.add('is-confirmed');
    }
    this.handleProofClose();
    // Lets a wrapping component (e.g. the stamp studio wizard) gate further
    // progress on proof review without this class needing to know anything
    // about what, if anything, contains it.
    this.dispatchEvent(new CustomEvent('stamp:proof-confirmed', { bubbles: true }));
  }

  /** Switches between "design with text" and "upload your own artwork". */
  #setMode(mode) {
    const upload = mode === 'upload';
    const { designPanel, uploadPanel, modeDesignBtn, modeUploadBtn } = this.refs;
    if (designPanel) designPanel.hidden = upload;
    if (uploadPanel) uploadPanel.hidden = !upload;
    modeDesignBtn?.classList.toggle('is-active', !upload);
    modeUploadBtn?.classList.toggle('is-active', upload);
    // The design-mode property is only meaningful for an uploaded design; when
    // designing with text the layout/text properties already describe it.
    this.#setProp(this.refs.uploadModeProp, 'properties[Design mode]', upload ? 'Custom uploaded design' : '');
    if (upload) {
      // Don't let the text design's properties ride along on an uploaded-design
      // order — clear them so the order only carries the uploaded artwork.
      this.#clearDesignProps();
    } else {
      this.#setProp(this.refs.uploadFileNameProp, 'properties[Uploaded file]', '');
      // Restore the text design's properties by re-rendering the active shape.
      this.#rerender();
    }
  }

  /** Clears every text-design line-item property (used when switching to upload). */
  #clearDesignProps() {
    [
      'designInput', 'layoutNoteInput', 'ringSizePropInput', 'centerSizePropInput',
      'line1TextProp', 'line2TextProp', 'centerTextProp', 'line3TextProp',
      'productionSvgInput', 'logoProp', 'proofImgInput',
    ].forEach((k) => this.#setProp(this.refs[k], '', ''));
  }

  /**
   * Rasterises the live preview to a small JPEG saved on the order as a quick
   * visual proof. Kept small (240px, compressed) and size-guarded so it never
   * risks the add-to-cart request; the exact artwork still travels in
   * _Design SVG. Web-font glyphs fall back to their same-category system font
   * in the raster, so the picture is a close approximation, not pixel-exact.
   */
  #generateProofImage() {
    const svg = this.refs.previewSvg;
    const input = this.refs.proofImgInput;
    if (!svg || !input || typeof document === 'undefined') return;
    try {
      const clone = /** @type {SVGSVGElement} */ (svg.cloneNode(true));
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.removeAttribute('ref');
      // The [hidden] layouts hide via CSS that won't exist in the isolated
      // raster, so collapse them inline; and pin the ink colour so currentColor
      // resolves without the stylesheet.
      clone.querySelectorAll('[hidden]').forEach((el) => {
        el.setAttribute('style', (el.getAttribute('style') || '') + ';display:none');
      });
      clone.style.color = this.#ink.hex;
      const str = new XMLSerializer().serializeToString(clone);
      const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(str);
      const img = new Image();
      img.onload = () => {
        const size = 240;
        const c = document.createElement('canvas');
        c.width = size;
        c.height = size;
        const ctx = c.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        let data = '';
        try {
          data = c.toDataURL('image/jpeg', 0.7);
        } catch (e) {
          return; // tainted canvas — skip rather than break anything
        }
        // Only attach if comfortably within a line-item-property value.
        this.#setProp(input, data && data.length < 60000 ? 'properties[_Proof image]' : '', data.length < 60000 ? data : '');
      };
      img.src = url;
    } catch (e) {
      /* no-op */
    }
  }

  handleModeUpload() {
    this.#setMode('upload');
  }

  handleModeDesign() {
    this.#setMode('design');
  }

  /** Shows a client-side preview of the shopper's uploaded artwork. */
  handleUploadChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files && input.files[0];
    const { uploadPreview, uploadHint } = this.refs;
    if (!file) return;

    if (file.type === 'application/pdf') {
      // PDFs can't render in an <img>; just confirm the file is attached.
      if (uploadPreview) uploadPreview.hidden = true;
      if (uploadHint) uploadHint.textContent = file.name + ' — PDF ready';
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        if (uploadPreview) {
          uploadPreview.src = /** @type {string} */ (reader.result);
          uploadPreview.hidden = false;
        }
        if (uploadHint) uploadHint.textContent = 'Change file';
      };
      reader.readAsDataURL(file);
    }

    this.#setProp(this.refs.uploadModeProp, 'properties[Design mode]', 'Custom uploaded design');
    this.#setProp(this.refs.uploadFileNameProp, 'properties[Uploaded file]', file.name);
  }

  handleLogoPick() {
    this.refs.logoInput?.click();
  }

  /** Reads an uploaded logo, downscales it for a light preview, and overlays it. */
  handleLogoChange(event) {
    const file = /** @type {HTMLInputElement} */ (event.target).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = /** @type {string} */ (reader.result);
      const img = new Image();
      img.onload = () => {
        const max = 300;
        const s = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(img.width * s));
        c.height = Math.max(1, Math.round(img.height * s));
        c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
        try {
          this.#logoHref = c.toDataURL('image/png');
        } catch (e) {
          this.#logoHref = src; // e.g. SVG tainting — fall back to the raw data URL
        }
        this.#renderLogo();
      };
      img.onerror = () => {
        this.#logoHref = src;
        this.#renderLogo();
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
    if (this.refs.logoControls) this.refs.logoControls.hidden = false;
    this.#setProp(this.refs.logoProp, 'properties[Logo]', 'Uploaded (' + file.name + ')');
  }

  handleLogoSize(event) {
    this.#logoScale = Number(/** @type {HTMLInputElement} */ (event.target).value) || 64;
    this.#renderLogo();
  }

  handleRemoveLogo() {
    this.#logoHref = '';
    if (this.refs.logoInput) this.refs.logoInput.value = '';
    if (this.refs.logoControls) this.refs.logoControls.hidden = true;
    this.#setProp(this.refs.logoProp, 'properties[Logo]', '');
    this.#renderLogo();
  }

  handleLogoPos(event) {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-pos]');
    if (!btn) return;
    this.#logoPos = /** @type {'top'|'center'|'bottom'} */ (btn.dataset.pos) || 'center';
    (this.refs.logoPosBtns ?? []).forEach((b) => b.classList.toggle('is-active', b === btn));
    this.#renderLogo();
  }

  /** Positions the logo overlay for the current shape + placement (or hides it). */
  #renderLogo() {
    const el = this.refs.logoImage;
    if (!el) return;
    if (!this.#logoHref) {
      setSvgHidden(el, true);
      return;
    }
    const w = this.#logoScale;
    const cx = 130;
    const rect = this.#shape === 'rectangle';
    let cy = 130;
    if (this.#logoPos === 'top') cy = rect ? 104 : 92;
    else if (this.#logoPos === 'bottom') cy = rect ? 158 : 170;
    el.setAttribute('href', this.#logoHref);
    el.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', this.#logoHref);
    el.setAttribute('x', String(cx - w / 2));
    el.setAttribute('y', String(cy - w / 2));
    el.setAttribute('width', String(w));
    el.setAttribute('height', String(w));
    setSvgHidden(el, false);
  }

  /**
   * Standalone-SVG equivalent of #renderLogo's positioning math, so the
   * exported production art (the merchant-only `_Design SVG` property)
   * actually includes an uploaded logo — previously it only ever appeared in
   * the live on-page preview and the raster proof snapshot.
   * @returns {string}
   */
  #logoMarkup() {
    if (!this.#logoHref) return '';
    const w = this.#logoScale;
    const cx = 130;
    const rect = this.#shape === 'rectangle';
    let cy = 130;
    if (this.#logoPos === 'top') cy = rect ? 104 : 92;
    else if (this.#logoPos === 'bottom') cy = rect ? 158 : 170;
    return `<image href="${this.#logoHref}" x="${cx - w / 2}" y="${cy - w / 2}" width="${w}" height="${w}" preserveAspectRatio="xMidYMid meet"/>`;
  }

  /** @param {HTMLInputElement} radio */
  #applyPreset(radio) {
    if (!radio.checked) radio.checked = true;

    const rawShape = radio.dataset.shape;
    const shape = rawShape === 'rectangle' ? 'rectangle' : rawShape === 'for' ? 'for' : 'round';
    const starLayout = radio.dataset.starLayout === 'double' ? 'double' : 'single';
    const lineCount = Number(radio.dataset.lines || '1');
    const isRound = shape === 'round';
    const isRectangle = shape === 'rectangle';
    const isFor = shape === 'for';
    const isDoubleStar = isRound && starLayout === 'double';

    this.#shape = shape;
    this.#starLayout = starLayout;
    this.#uppercase = radio.dataset.uppercase === 'true';
    this.#designationText = radio.dataset.designationText ?? '';
    this.#placeholders = [
      radio.dataset.line1Placeholder ?? '',
      radio.dataset.line2Placeholder ?? '',
      radio.dataset.line3Placeholder ?? '',
    ];

    const {
      designInput, layoutNoteInput, rectGroup, roundGroup, forGroup, sizeControls,
      lineInput1, lineInput2, lineInput3, centerInput,
      fieldWrapper1, fieldWrapper2, fieldWrapper3, fieldWrapperCenter,
      fieldLabel1, fieldLabel2, fieldLabelCenter,
    } = this.refs;

    if (designInput) designInput.value = radio.dataset.presetName ?? '';
    if (layoutNoteInput) {
      layoutNoteInput.value = isDoubleStar
        ? 'Round: text curves along the top and bottom borders in two separate arcs, split by a star fixed at each end of the horizontal center line; center text is centered inside the inner circle and wraps onto extra lines before shrinking.'
        : isRound
          ? 'Round: ring text curves along the top border; center text is centered inside the inner circle and wraps onto extra lines before shrinking; a star fills the gap at the bottom.'
          : isFor
            ? '"For" stamp: "For" is fixed, followed by the typed company/name; the designation is fixed by the chosen preset and sits at the bottom right.'
            : 'Rectangle: straight horizontal text lines, stacked and centered.';
    }
    // `hidden` is an HTMLElement property; SVG elements don't implement it, so
    // `svgGroup.hidden = true` silently sets a stray JS expando and never
    // touches the attribute the stylesheet actually keys off. These three
    // groups are <g> elements, so their visibility has to be toggled through
    // the attribute itself. (sizeControls below is a real HTML element, where
    // the property works fine.)
    setSvgHidden(rectGroup, !isRectangle);
    setSvgHidden(roundGroup, !isRound);
    setSvgHidden(forGroup, !isFor);
    if (sizeControls) sizeControls.hidden = !isRound;

    if (isRound) this.#buildStars(isDoubleStar ? 'double' : 'single');

    if (isRound) {
      if (lineInput1) {
        lineInput1.value = '';
        lineInput1.placeholder = this.#placeholders[0] ?? '';
      }
      if (fieldWrapper1) fieldWrapper1.hidden = false;
      if (fieldLabel1) fieldLabel1.textContent = isDoubleStar ? 'Top text' : 'Outer ring text';

      if (lineInput2) {
        lineInput2.value = '';
        lineInput2.placeholder = isDoubleStar ? this.#placeholders[1] ?? '' : '';
      }
      if (fieldWrapper2) fieldWrapper2.hidden = !isDoubleStar;
      if (fieldLabel2) fieldLabel2.textContent = 'Bottom text';

      if (fieldWrapper3) fieldWrapper3.hidden = true;

      const centerPlaceholderIndex = isDoubleStar ? 2 : 1;
      if (centerInput) {
        centerInput.value = '';
        centerInput.placeholder = this.#placeholders[centerPlaceholderIndex] ?? '';
      }
      if (fieldWrapperCenter) fieldWrapperCenter.hidden = false;
      if (fieldLabelCenter) fieldLabelCenter.textContent = 'Circle text';

      if (this.refs.ringSizeInput) this.refs.ringSizeInput.value = '11';
      if (this.refs.centerSizeInput) this.refs.centerSizeInput.value = '11';
      if (this.refs.linkSizes) this.refs.linkSizes.checked = true;
      this.#renderRound();
    } else if (isFor) {
      if (lineInput1) {
        lineInput1.value = '';
        lineInput1.placeholder = this.#placeholders[0] ?? '';
      }
      if (fieldWrapper1) fieldWrapper1.hidden = false;
      if (fieldLabel1) fieldLabel1.textContent = 'Company / name';

      if (fieldWrapper2) fieldWrapper2.hidden = true;
      if (fieldWrapperCenter) fieldWrapperCenter.hidden = true;
      if (fieldWrapper3) fieldWrapper3.hidden = true;

      this.#renderFor();
    } else {
      if (fieldWrapperCenter) fieldWrapperCenter.hidden = true;

      for (let i = 1; i <= 3; i += 1) {
        const input = this.refs[`lineInput${i}`];
        const wrapper = this.refs[`fieldWrapper${i}`];
        const label = this.refs[`fieldLabel${i}`];

        if (!input) continue;

        input.value = '';
        input.placeholder = this.#placeholders[i - 1] ?? '';

        const visible = i <= lineCount;
        if (wrapper) wrapper.hidden = !visible;
        if (label) label.textContent = `Line ${i}`;
      }

      this.#renderRectangle();
    }
  }

  /**
   * Rebuilds the star(s) from scratch for `layout` rather than toggling
   * visibility on pre-baked groups, so there's no stale hidden/shown state
   * that can get stuck on the wrong group.
   * @param {'single' | 'double'} layout
   */
  #buildStars(layout) {
    const { starsGroup } = this.refs;
    if (!starsGroup) return;

    starsGroup.replaceChildren();

    const positions = layout === 'double' ? STAR_DOUBLE_POSITIONS : STAR_SINGLE_POSITIONS;
    positions.forEach(([x, y]) => {
      const group = document.createElementNS(SVG_NS, 'g');
      group.setAttribute('class', 'stamp-customizer__star');
      group.setAttribute('transform', `translate(${x},${y})`);
      const polygon = document.createElementNS(SVG_NS, 'polygon');
      polygon.setAttribute('points', STAR_POINTS);
      group.appendChild(polygon);
      starsGroup.appendChild(group);
    });
  }

  /**
   * Sets (or clears) a hidden line-item-property input. Clearing removes the
   * `name` attribute entirely rather than submitting an empty value, so an
   * un-personalized field doesn't show up as a blank line on the order.
   * @param {HTMLInputElement | undefined} input
   * @param {string} name
   * @param {string} value
   */
  #setProp(input, name, value) {
    if (!input) return;

    if (value) {
      input.setAttribute('name', name);
      input.value = value;
    } else {
      input.removeAttribute('name');
      input.value = '';
    }
  }

  /** @param {string} str */
  #applyCase(str) {
    return this.#uppercase ? str.toUpperCase() : str;
  }

  /** @param {number} pt */
  #ptToPx(pt) {
    return pt * PT_TO_PX;
  }

  /** @param {string} str */
  #escapeXml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /**
   * @param {string} str
   * @param {number} pt
   */
  #measureRingWidth(str, pt) {
    const { ringMeasure } = this.refs;
    ringMeasure.setAttribute('font-size', String(this.#ptToPx(pt)));
    ringMeasure.textContent = str;
    return ringMeasure.getComputedTextLength();
  }

  /**
   * @param {string} str
   * @param {number} pt
   */
  #measureCenterWidth(str, pt) {
    const { centerMeasure } = this.refs;
    centerMeasure.setAttribute('font-size', String(this.#ptToPx(pt)));
    centerMeasure.textContent = str;
    return centerMeasure.getComputedTextLength();
  }

  /**
   * Trims from the end until the (cased) string fits within `maxLength` at `pt`
   * — the arc simply stops accepting keystrokes once it's physically full at
   * this point size, the same way a fixed-size engraving would, rather than
   * squeezing glyphs to fit.
   * @param {string} str
   * @param {number} pt
   * @param {number} maxLength
   */
  #fitArcLength(str, pt, maxLength) {
    let n = str.length;
    while (n > 0 && this.#measureRingWidth(this.#applyCase(str.slice(0, n)), pt) > maxLength) {
      n -= 1;
    }
    return n;
  }

  /**
   * Computes {char, x, y, deg} for each character of `text` positioned along the
   * ring arc, centered on `centerAngleDeg` (clockwise degrees from the top, so 0
   * is the top arc's midpoint and 180 is the bottom arc's midpoint). Shared by
   * the live preview (DOM glyphs) and the production SVG export (string glyphs)
   * so both always agree exactly on layout.
   *
   * `flip` rotates each glyph an extra 180deg so its "up" points toward the
   * circle's center instead of away from it — needed for a bottom arc so the
   * text reads upright to the viewer instead of upside-down, and reads
   * left-to-right (the direction of travel around the arc also reverses to
   * match, since the arc is traced from the reader's left to their right).
   * Returns `{ glyphs, totalWidth }` — `totalWidth` is the summed pixel width
   * of `text` at `pt`, exposed so callers can reuse it for a fit/overflow
   * check without paying for a further separate measurement pass.
   * @param {string} text
   * @param {number} pt
   * @param {number} centerAngleDeg
   * @param {boolean} flip
   */
  #computeArcGlyphs(text, pt, centerAngleDeg, flip) {
    const { ringMeasure } = this.refs;
    const px = this.#ptToPx(pt);
    ringMeasure.setAttribute('font-size', String(px));

    const chars = Array.from(text);
    const widths = chars.map((ch) => {
      ringMeasure.textContent = ch;
      return ringMeasure.getComputedTextLength();
    });
    const totalWidth = widths.reduce((sum, w) => sum + w, 0);
    const totalAngle = totalWidth / RING_RADIUS; // radians
    const centerAngle = (centerAngleDeg * Math.PI) / 180;
    const sign = flip ? -1 : 1;
    let cursor = -totalAngle / 2; // center the text on the arc's midpoint

    const glyphs = chars.map((ch, i) => {
      const w = widths[i];
      const charOffset = cursor + w / 2 / RING_RADIUS;
      cursor += w / RING_RADIUS;
      const theta = centerAngle + sign * charOffset;
      const thetaDeg = (theta * 180) / Math.PI;
      const deg = flip ? thetaDeg + 180 : thetaDeg;
      const x = RING_CX + RING_RADIUS * Math.sin(theta);
      const y = RING_CY - RING_RADIUS * Math.cos(theta);
      return { ch, x, y, deg };
    });

    return { glyphs, totalWidth };
  }

  /**
   * Renders precomputed glyphs (from #computeArcGlyphs) as individual <text>
   * elements positioned and rotated along the ring's arc, replacing the
   * previous SVG textPath approach. textPath support (font metrics, baseline
   * alignment, spacing) varies across vector tools, so plain positioned/rotated
   * text is the more portable choice for a design that eventually gets
   * recreated in CorelDRAW. Takes already-computed glyphs (rather than
   * computing them itself) so a caller that also needs the same glyphs for the
   * production SVG export can measure once and reuse the result.
   * @param {SVGGElement | undefined} group
   * @param {{ch: string, x: number, y: number, deg: number}[]} glyphs
   * @param {number} pt
   */
  #buildArcGlyphs(group, glyphs, pt) {
    if (!group) return;

    const px = this.#ptToPx(pt);

    group.replaceChildren();

    glyphs.forEach(({ ch, x, y, deg }) => {
      const glyph = document.createElementNS(SVG_NS, 'text');
      glyph.setAttribute('x', String(x));
      glyph.setAttribute('y', String(y));
      glyph.setAttribute('transform', `rotate(${deg} ${x} ${y})`);
      glyph.setAttribute('text-anchor', 'middle');
      // Centers the glyph vertically on (x,y) — which sits exactly on the ring
      // radius — instead of anchoring its baseline there. Baseline anchoring
      // left both arcs' text sitting off to one side of the ring (and the
      // offset grew with font size); dominant-baseline handles the vertical
      // centering using real font metrics, and stays centered under rotation
      // since the rotate transform pivots on this same (x,y) point.
      glyph.setAttribute('dominant-baseline', 'central');
      glyph.setAttribute('font-size', String(px));
      glyph.setAttribute('class', 'stamp-customizer__ring-text');
      // Preserves a lone space glyph instead of letting SVG's default
      // whitespace handling collapse it away to zero width.
      glyph.setAttribute('xml:space', 'preserve');
      glyph.textContent = ch;
      group.appendChild(glyph);
    });
  }

  /**
   * @param {{ch: string, x: number, y: number, deg: number}[]} glyphs
   */
  #glyphMarkup(glyphs) {
    const r1 = (n) => Math.round(n * 10) / 10;
    return glyphs
      .map(({ ch, x, y, deg }) => {
        const rx = r1(x);
        const ry = r1(y);
        const rd = r1(deg);
        return `<text x="${rx}" y="${ry}" transform="rotate(${rd} ${rx} ${ry})" dominant-baseline="central" xml:space="preserve">${this.#escapeXml(ch)}</text>`;
      })
      .join('');
  }

  /**
   * Greedy word-wraps `text` (already case-converted) into lines that each fit
   * within CENTER_MAX_LENGTH at `pt`, honoring literal newlines (from the
   * shopper pressing Enter in the textarea) as forced line breaks. A single
   * word wider than the limit on its own is hard-broken character by
   * character rather than left to overflow.
   * @param {string} text
   * @param {number} pt
   */
  #wrapCenterLines(text, pt) {
    const lines = [];

    text.split('\n').forEach((paragraph) => {
      const words = paragraph.split(' ');
      let current = '';

      words.forEach((word) => {
        let remaining = word;

        // Guards against an unbreakable single leftover character (length 1)
        // ever entering this loop, which would otherwise spin forever since a
        // lone character can't be cut any further.
        while (remaining.length > 1 && this.#measureCenterWidth(remaining, pt) > CENTER_MAX_LENGTH) {
          let cut = remaining.length - 1;
          while (cut > 1 && this.#measureCenterWidth(remaining.slice(0, cut), pt) > CENTER_MAX_LENGTH) {
            cut -= 1;
          }
          if (current) {
            lines.push(current);
            current = '';
          }
          lines.push(remaining.slice(0, cut));
          remaining = remaining.slice(cut);
        }

        const candidate = current ? `${current} ${remaining}` : remaining;
        if (current && this.#measureCenterWidth(candidate, pt) > CENTER_MAX_LENGTH) {
          lines.push(current);
          current = remaining;
        } else {
          current = candidate;
        }
      });

      lines.push(current);
    });

    return lines;
  }

  /**
   * @param {string} text - already case-converted
   * @param {number} pt
   */
  #centerFits(text, pt) {
    return this.#wrapCenterLines(text, pt).length <= MAX_CENTER_LINES;
  }

  /**
   * Finds the largest point size (searching downward from `startPt` only —
   * matches the ring/arc fields' "only shrinks, never grows back" behavior)
   * at which `raw` wraps into at most MAX_CENTER_LINES lines, trimming
   * characters from the end if even the smallest size doesn't fit. This is
   * what makes wrapping the first response to overflow and shrinking the
   * fallback: at a given size, more text just claims another line, and the
   * point size only drops once every available line is already spoken for.
   * @param {string} raw
   * @param {number} startPt
   */
  #fitCenterText(raw, startPt) {
    let ptIndex = POINT_SIZES.indexOf(startPt);
    if (ptIndex === -1) ptIndex = POINT_SIZES.length - 1;

    while (ptIndex > 0 && !this.#centerFits(this.#applyCase(raw), POINT_SIZES[ptIndex])) {
      ptIndex -= 1;
    }
    const pt = POINT_SIZES[ptIndex];

    let text = raw;
    while (text.length > 0 && !this.#centerFits(this.#applyCase(text), pt)) {
      text = text.slice(0, -1);
    }

    return { text, pt };
  }

  /**
   * Renders `lines` as a vertically-centered block of <text> elements, one per
   * line, each horizontally centered (text-anchor) and vertically centered on
   * its own slot via dominant-baseline — letting the browser's real font
   * metrics center the glyphs instead of a hand-picked baseline offset, which
   * previously sat noticeably high for all-caps text with no descenders.
   * @param {SVGGElement | undefined} group
   * @param {string[]} lines
   * @param {number} pt
   */
  #buildCenterLines(group, lines, pt) {
    if (!group) return;

    const px = this.#ptToPx(pt);
    const lineHeight = px * CENTER_LINE_HEIGHT_EM;
    const n = lines.length;

    group.replaceChildren();

    lines.forEach((line, i) => {
      const y = CENTER_CY + (i - (n - 1) / 2) * lineHeight;
      const el = document.createElementNS(SVG_NS, 'text');
      el.setAttribute('x', String(CENTER_CX));
      el.setAttribute('y', String(y));
      el.setAttribute('text-anchor', 'middle');
      el.setAttribute('dominant-baseline', 'central');
      el.setAttribute('font-size', String(px));
      el.setAttribute('class', 'stamp-customizer__center-text');
      el.setAttribute('xml:space', 'preserve');
      el.textContent = line;
      group.appendChild(el);
    });
  }

  /**
   * @param {string[]} lines
   * @param {number} pt
   */
  #centerLinesMarkup(lines, pt) {
    const r1 = (n) => Math.round(n * 10) / 10;
    const px = r1(this.#ptToPx(pt));
    const lineHeight = px * CENTER_LINE_HEIGHT_EM;
    const n = lines.length;

    return lines
      .map((line, i) => {
        const y = r1(CENTER_CY + (i - (n - 1) / 2) * lineHeight);
        return `<text x="${CENTER_CX}" y="${y}" text-anchor="middle" dominant-baseline="central" font-size="${px}" xml:space="preserve">${this.#escapeXml(line)}</text>`;
      })
      .join('');
  }

  /**
   * Builds a standalone SVG string (explicit colors/fonts, no CSS variables or
   * classes) for the single-star round layout, suitable for pasting straight
   * into a design tool. `ringGlyphs` uses the same {ch,x,y,deg} shape as
   * #computeArcGlyphs.
   * @param {{ch: string, x: number, y: number, deg: number}[]} ringGlyphs
   * @param {number} ringPt
   * @param {string[]} centerLines
   * @param {number} centerPt
   */
  #buildProductionSVGRound(ringGlyphs, ringPt, centerLines, centerPt) {
    const r1 = (n) => Math.round(n * 10) / 10;
    const ringPx = r1(this.#ptToPx(ringPt));
    const ink = this.#ink.hex;
    const font = this.#font.css;

    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 260" width="260" height="260">' +
      `<circle cx="130" cy="130" r="116" fill="none" stroke="${ink}" stroke-width="2"/>` +
      `<circle cx="130" cy="130" r="112" fill="none" stroke="${ink}" stroke-width="2"/>` +
      `<circle cx="130" cy="130" r="74" fill="none" stroke="${ink}" stroke-width="2"/>` +
      `<g font-family="${font}" font-weight="bold" fill="${ink}" text-anchor="middle">` +
      `<g font-size="${ringPx}">${this.#glyphMarkup(ringGlyphs)}</g>` +
      this.#centerLinesMarkup(centerLines, centerPt) +
      '</g>' +
      `<g transform="translate(130,225)"><polygon points="${STAR_POINTS}" fill="${ink}"/></g>` +
      this.#logoMarkup() +
      '</svg>'
    );
  }

  /**
   * Builds a standalone SVG string for the two-star round layout: top and
   * bottom text arcs, a center line, and stars fixed at the two ends of the
   * horizontal diameter.
   * @param {{ch: string, x: number, y: number, deg: number}[]} topGlyphs
   * @param {{ch: string, x: number, y: number, deg: number}[]} bottomGlyphs
   * @param {number} ringPt
   * @param {string[]} centerLines
   * @param {number} centerPt
   */
  #buildProductionSVGRoundDouble(topGlyphs, bottomGlyphs, ringPt, centerLines, centerPt) {
    const r1 = (n) => Math.round(n * 10) / 10;
    const ringPx = r1(this.#ptToPx(ringPt));
    const ink = this.#ink.hex;
    const font = this.#font.css;

    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 260" width="260" height="260">' +
      `<circle cx="130" cy="130" r="116" fill="none" stroke="${ink}" stroke-width="2"/>` +
      `<circle cx="130" cy="130" r="112" fill="none" stroke="${ink}" stroke-width="2"/>` +
      `<circle cx="130" cy="130" r="74" fill="none" stroke="${ink}" stroke-width="2"/>` +
      `<g font-family="${font}" font-weight="bold" fill="${ink}" text-anchor="middle">` +
      `<g font-size="${ringPx}">${this.#glyphMarkup(topGlyphs)}${this.#glyphMarkup(bottomGlyphs)}</g>` +
      this.#centerLinesMarkup(centerLines, centerPt) +
      '</g>' +
      `<g transform="translate(225,130)"><polygon points="${STAR_POINTS}" fill="${ink}"/></g>` +
      `<g transform="translate(35,130)"><polygon points="${STAR_POINTS}" fill="${ink}"/></g>` +
      this.#logoMarkup() +
      '</svg>'
    );
  }

  /**
   * @param {{text: string, y: number}[]} lines
   */
  #buildProductionSVGRectangle(lines) {
    const ink = this.#ink.hex;
    const font = this.#font.css;
    const textMarkup = lines
      .map(({ text, y }) => `<text x="130" y="${y}">${this.#escapeXml(text)}</text>`)
      .join('');

    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 260" width="260" height="260">' +
      `<rect x="20" y="85" width="220" height="90" fill="none" stroke="${ink}" stroke-width="3"/>` +
      `<g font-family="${font}" font-weight="bold" fill="${ink}" text-anchor="middle" font-size="15">` +
      textMarkup +
      '</g>' +
      this.#logoMarkup() +
      '</svg>'
    );
  }

  /**
   * @param {string} name - the typed (cased) company/name text
   * @param {string} designationText - fixed per preset, not shopper-editable
   */
  #buildProductionSVGFor(name, designationText) {
    const ink = this.#ink.hex;
    const font = this.#font.css;
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 260" width="260" height="260">' +
      `<g font-family="${font}" fill="${ink}">` +
      `<text x="20" y="122" font-size="15" text-anchor="start">For ${this.#escapeXml(name)}</text>` +
      `<text x="240" y="158" font-size="14" text-anchor="end">${this.#escapeXml(designationText)}</text>` +
      '</g>' +
      this.#logoMarkup() +
      '</svg>'
    );
  }

  /**
   * @param {HTMLInputElement | undefined} input
   * @param {number} maxLength
   */
  #handleArcTyping(input, maxLength) {
    if (!input) return;

    const raw = input.value;
    const pt = Number(this.refs.ringSizeInput?.value || 11);
    const fitLength = this.#fitArcLength(raw, pt, maxLength);

    if (fitLength < raw.length) input.value = raw.slice(0, fitLength);

    this.#renderRound();
  }

  /** @param {HTMLTextAreaElement | undefined} input */
  #handleCenterTyping(input) {
    if (!input) return;

    const raw = input.value;
    const startPt = Number(this.refs.centerSizeInput?.value || POINT_SIZES[POINT_SIZES.length - 1]);
    const { text, pt } = this.#fitCenterText(raw, startPt);

    if (text !== raw) input.value = text;

    this.#applySize('center', pt, { fromTyping: true });
  }

  /**
   * Applies a size change from `origin` ('ring' or 'center'). When linked, both
   * dropdowns move together. The ring/arc input(s) always re-fit (trim) their
   * own text at whatever size they end up at. The center text does the same
   * UNLESS this change came from typing (fromTyping) — then the size was
   * already chosen so the typed text wraps to fit, so it's left alone; a size
   * change from the dropdown keeps the requested size fixed and trims the
   * center text from the end if it no longer wraps within the line limit,
   * since the shopper explicitly asked for that size.
   * @param {'ring' | 'center'} origin
   * @param {number} pt
   * @param {{ fromTyping?: boolean }} opts
   */
  #applySize(origin, pt, opts) {
    const { ringSizeInput, centerSizeInput, linkSizes } = this.refs;
    if (!ringSizeInput || !centerSizeInput) return;

    if (linkSizes?.checked) {
      ringSizeInput.value = String(pt);
      centerSizeInput.value = String(pt);
    } else if (origin === 'ring') {
      ringSizeInput.value = String(pt);
    } else {
      centerSizeInput.value = String(pt);
    }

    const ringPt = Number(ringSizeInput.value);
    const isDoubleStar = this.#starLayout === 'double';
    const arcInputs = isDoubleStar
      ? [this.refs.lineInput1, this.refs.lineInput2]
      : [this.refs.lineInput1];
    const arcMaxLength = isDoubleStar ? DOUBLE_STAR_MAX_LENGTH : SINGLE_STAR_MAX_LENGTH;

    arcInputs.forEach((arcInput) => {
      if (!arcInput) return;
      const arcRaw = arcInput.value;
      const arcFit = this.#fitArcLength(arcRaw, ringPt, arcMaxLength);
      if (arcFit < arcRaw.length) arcInput.value = arcRaw.slice(0, arcFit);
    });

    const centerInput = this.refs.centerInput;
    if (centerInput && !opts.fromTyping) {
      const centerPt = Number(centerSizeInput.value);
      let text = centerInput.value;
      while (text.length > 0 && !this.#centerFits(this.#applyCase(text), centerPt)) {
        text = text.slice(0, -1);
      }
      if (text !== centerInput.value) centerInput.value = text;
    }

    this.#renderRound();
  }

  #renderRound() {
    if (this.#starLayout === 'double') {
      this.#renderRoundDouble();
    } else {
      this.#renderRoundSingle();
    }
    this.#renderLogo();
  }

  #renderRoundSingle() {
    const {
      ringTextGroup, ringTextGroup2, centerTextGroup,
      ringSizeInput, centerSizeInput,
      ringSizePropInput, centerSizePropInput,
      line1TextProp, centerTextProp,
      productionSvgInput,
      fitNote, fitNoteText,
      lineInput1, centerInput,
    } = this.refs;

    if (!ringTextGroup || !centerTextGroup) return;

    // The two-star layout's bottom arc lives in this same sibling group;
    // clear it out so switching back to single-star doesn't leave its last
    // rendered text stuck on screen (nothing else here ever re-renders it).
    if (ringTextGroup2) ringTextGroup2.replaceChildren();

    const ringPt = Number(ringSizeInput?.value || 11);
    const centerPt = Number(centerSizeInput?.value || 11);

    this.#setProp(ringSizePropInput, 'properties[Ring Text Size]', `${ringPt}pt`);
    this.#setProp(centerSizePropInput, 'properties[Circle Text Size]', `${centerPt}pt`);

    const ringRaw = lineInput1?.value ?? '';
    const centerRaw = centerInput?.value ?? '';
    // The submitted property is the exact final (cased) text, or empty if
    // nothing was typed — never the placeholder sample text.
    const ringSubmit = this.#applyCase(ringRaw);
    const centerSubmit = this.#applyCase(centerRaw);
    const ringDisplay = ringSubmit || this.#placeholders[0];
    const centerDisplayCased = centerSubmit || this.#applyCase(this.#placeholders[1]);

    this.#setProp(line1TextProp, 'properties[Ring Text]', ringSubmit);
    this.#setProp(centerTextProp, 'properties[Center Text]', centerSubmit);

    const ringComputed = this.#computeArcGlyphs(ringDisplay, ringPt, 0, false);
    this.#buildArcGlyphs(ringTextGroup, ringComputed.glyphs, ringPt);

    const centerLines = this.#wrapCenterLines(centerDisplayCased, centerPt);
    this.#buildCenterLines(centerTextGroup, centerLines, centerPt);

    if (productionSvgInput) {
      const svg =
        ringSubmit || centerSubmit
          ? this.#buildProductionSVGRound(
              ringSubmit === ringDisplay ? ringComputed.glyphs : this.#computeArcGlyphs(ringSubmit, ringPt, 0, false).glyphs,
              ringPt,
              centerSubmit ? this.#wrapCenterLines(centerSubmit, centerPt) : [],
              centerPt,
            )
          : '';
      // Drop just the embedded logo image if the property value would come in
      // too large — same 60,000-char ceiling as the proof image guard — so an
      // oversize logo never risks the whole design/order, only its own overlay.
      const finalSvg = svg.length >= 60000 ? svg.replace(/<image[^>]*\/>/, '') : svg;
      this.#setProp(productionSvgInput, 'properties[_Design SVG]', finalSvg);
    }

    const ringFull = ringRaw.length > 0 && ringComputed.totalWidth >= SINGLE_STAR_MAX_LENGTH - 1;
    const centerAtFloor = centerPt === POINT_SIZES[0] && centerRaw.length > 0 && centerLines.length >= MAX_CENTER_LINES;

    if (fitNote) fitNote.classList.toggle('stamp-customizer__fit-note--active', ringFull || centerAtFloor);
    if (fitNoteText) {
      fitNoteText.textContent = ringFull
        ? 'Ring is full at this text size'
        : centerAtFloor
          ? 'Circle is full at the smallest text size'
          : '';
    }
  }

  #renderRoundDouble() {
    const {
      ringTextGroup, ringTextGroup2, centerTextGroup,
      ringSizeInput, centerSizeInput,
      ringSizePropInput, centerSizePropInput,
      line1TextProp, line2TextProp, centerTextProp,
      productionSvgInput,
      fitNote, fitNoteText,
      lineInput1, lineInput2, centerInput,
    } = this.refs;

    if (!ringTextGroup || !ringTextGroup2 || !centerTextGroup) return;

    const ringPt = Number(ringSizeInput?.value || 11);
    const centerPt = Number(centerSizeInput?.value || 11);

    this.#setProp(ringSizePropInput, 'properties[Ring Text Size]', `${ringPt}pt`);
    this.#setProp(centerSizePropInput, 'properties[Circle Text Size]', `${centerPt}pt`);

    const topRaw = lineInput1?.value ?? '';
    const bottomRaw = lineInput2?.value ?? '';
    const centerRaw = centerInput?.value ?? '';
    // The submitted property is the exact final (cased) text, or empty if
    // nothing was typed — never the placeholder sample text.
    const topSubmit = this.#applyCase(topRaw);
    const bottomSubmit = this.#applyCase(bottomRaw);
    const centerSubmit = this.#applyCase(centerRaw);
    const topDisplay = topSubmit || this.#placeholders[0];
    const bottomDisplay = bottomSubmit || this.#placeholders[1];
    const centerDisplayCased = centerSubmit || this.#applyCase(this.#placeholders[2]);

    this.#setProp(line1TextProp, 'properties[Top Text]', topSubmit);
    this.#setProp(line2TextProp, 'properties[Bottom Text]', bottomSubmit);
    this.#setProp(centerTextProp, 'properties[Center Text]', centerSubmit);

    const topComputed = this.#computeArcGlyphs(topDisplay, ringPt, 0, false);
    const bottomComputed = this.#computeArcGlyphs(bottomDisplay, ringPt, 180, true);
    this.#buildArcGlyphs(ringTextGroup, topComputed.glyphs, ringPt);
    this.#buildArcGlyphs(ringTextGroup2, bottomComputed.glyphs, ringPt);

    const centerLines = this.#wrapCenterLines(centerDisplayCased, centerPt);
    this.#buildCenterLines(centerTextGroup, centerLines, centerPt);

    if (productionSvgInput) {
      const svg =
        topSubmit || bottomSubmit || centerSubmit
          ? this.#buildProductionSVGRoundDouble(
              topSubmit === topDisplay ? topComputed.glyphs : this.#computeArcGlyphs(topSubmit, ringPt, 0, false).glyphs,
              bottomSubmit === bottomDisplay ? bottomComputed.glyphs : this.#computeArcGlyphs(bottomSubmit, ringPt, 180, true).glyphs,
              ringPt,
              centerSubmit ? this.#wrapCenterLines(centerSubmit, centerPt) : [],
              centerPt,
            )
          : '';
      const finalSvg = svg.length >= 60000 ? svg.replace(/<image[^>]*\/>/, '') : svg;
      this.#setProp(productionSvgInput, 'properties[_Design SVG]', finalSvg);
    }

    const topFull = topRaw.length > 0 && topComputed.totalWidth >= DOUBLE_STAR_MAX_LENGTH - 1;
    const bottomFull = bottomRaw.length > 0 && bottomComputed.totalWidth >= DOUBLE_STAR_MAX_LENGTH - 1;
    const centerAtFloor = centerPt === POINT_SIZES[0] && centerRaw.length > 0 && centerLines.length >= MAX_CENTER_LINES;

    if (fitNote) fitNote.classList.toggle('stamp-customizer__fit-note--active', topFull || bottomFull || centerAtFloor);
    if (fitNoteText) {
      fitNoteText.textContent = topFull
        ? 'Top text is full at this text size'
        : bottomFull
          ? 'Bottom text is full at this text size'
          : centerAtFloor
            ? 'Circle is full at the smallest text size'
            : '';
    }
  }

  #renderRectangle() {
    const productionLines = [];

    for (let i = 1; i <= 3; i += 1) {
      const input = this.refs[`lineInput${i}`];
      const wrapper = this.refs[`fieldWrapper${i}`];
      const previewText = this.refs[`previewLine${i}`];
      const propInput = this.refs[`line${i}TextProp`];

      if (!previewText) continue;

      const active = Boolean(input) && Boolean(wrapper) && !wrapper.hidden;
      const typed = active ? input.value : '';
      const raw = typed || (active ? this.#placeholders[i - 1] : '');
      const submitValue = this.#applyCase(typed);

      previewText.textContent = this.#applyCase(raw);
      previewText.classList.toggle('stamp-customizer__text--placeholder', active && !typed);

      this.#setProp(propInput, `properties[Line ${i}]`, submitValue);

      if (active && submitValue) {
        productionLines.push({ text: submitValue, y: RECT_LINE_Y[i - 1] });
      }
    }

    if (this.refs.productionSvgInput) {
      const svg = productionLines.length ? this.#buildProductionSVGRectangle(productionLines) : '';
      const finalSvg = svg.length >= 60000 ? svg.replace(/<image[^>]*\/>/, '') : svg;
      this.#setProp(this.refs.productionSvgInput, 'properties[_Design SVG]', finalSvg);
    }

    this.#renderLogo();
  }

  /**
   * Width of the rendered "For <name>" line, in viewBox units. Measured on
   * forText itself (rather than an off-canvas node) so the measurement uses the
   * very font and size the line is actually drawn in. Safe to clobber the
   * element's text here: every caller rewrites it immediately afterwards.
   * @param {string} name - already case-converted
   */
  #measureForWidth(name) {
    const { forText } = this.refs;
    forText.textContent = `For ${name}`;
    return forText.getComputedTextLength();
  }

  #renderFor() {
    const { forText, forDesignationText, lineInput1, line1TextProp, productionSvgInput } = this.refs;

    if (!forText || !forDesignationText) return;

    // Trim what was typed down to what physically fits on the die. Only the
    // typed value is trimmed — a long placeholder is just sample text and never
    // ends up on the order.
    if (lineInput1) {
      let fitted = lineInput1.value;
      while (fitted.length > 0 && this.#measureForWidth(this.#applyCase(fitted)) > FOR_TEXT_MAX_WIDTH) {
        fitted = fitted.slice(0, -1);
      }
      if (fitted !== lineInput1.value) lineInput1.value = fitted;
    }

    const typed = lineInput1?.value ?? '';
    const raw = typed || this.#placeholders[0];
    // "For " is a literal, un-cased prefix — only the typed company/name follows
    // the preset's uppercase setting, matching the reference stamps where "For"
    // stays mixed-case even though the company name is all caps.
    const displayName = this.#applyCase(raw);
    const submitName = this.#applyCase(typed);

    forText.textContent = `For ${displayName}`;
    forText.classList.toggle('stamp-customizer__text--placeholder', !typed);
    forDesignationText.textContent = this.#designationText;

    this.#setProp(line1TextProp, 'properties[Company / Name]', submitName);

    if (productionSvgInput) {
      const svg = submitName ? this.#buildProductionSVGFor(submitName, this.#designationText) : '';
      const finalSvg = svg.length >= 60000 ? svg.replace(/<image[^>]*\/>/, '') : svg;
      this.#setProp(productionSvgInput, 'properties[_Design SVG]', finalSvg);
    }

    this.#renderLogo();
  }
}

customElements.define('stamp-customizer-component', StampCustomizer);
